<syrf-dialog-layout>
  <form [formGroup]="createProjectForm">
    <mat-horizontal-stepper linear #stepper>
      <mat-step
        fxLayout="column"
        label="Basic Details"
        formGroupName="basic"
        [stepControl]="createProjectForm.get('basic')"
      >
        <div fxLayout="column">
          <mat-form-field appearance="outline">
            <mat-label>Project Name</mat-label>
            <input
              matInput
              required
              placeholder="Project Name"
              formControlName="name"
              data-testid="wizard-project-name-input"
            />
            @if (createProjectForm.get('basic.name').errors === null) {
            <mat-icon
              matSuffix
              matTooltip="Unique Project Name"
              style="color: darkgreen; font-weight: bold"
              >check</mat-icon
            >
            } @for ( error of $any(createProjectForm.get('basic.name').errors) |
            keyvalue ; track error) {
            <mat-error>
              {{
                ProjectNameValidator.errorDescription[error.key] || error.key
              }}
            </mat-error>
            }
            <!--            <mat-error *ngIf="createProjectForm.get('basic.name')?.errors?.hasWhitespace as error">-->
            <!--              {{error.value}}-->
            <!--            </mat-error>-->
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Protocol Link</mat-label>
            <input
              matInput
              placeholder="Protocol Link"
              formControlName="protocolLink"
            />
            <mat-hint
              >We strongly recommend that you register your Systematic Review
              prior to data collection and analysis. This may conveniently be
              done at
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.crd.york.ac.uk/PROSPERO"
              >
                <strong class="text-success">PROSPERO</strong>.
              </a></mat-hint
            >
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Description</mat-label>
            <textarea
              matInput
              placeholder="Description"
              formControlName="description"
              data-testid="wizard-project-description"
            >
            </textarea>
          </mat-form-field>
          <mat-form-field appearance="outline" floatLabel="always">
            <mat-label>Keywords</mat-label>
            <app-autocomplete-chiplist
              formControlName="keywords"
              addLabel="New keyword..."
              [options]="keywordOptions$ | async"
            >
            </app-autocomplete-chiplist>
            <mat-hint>Tag your project with keywords for indexing</mat-hint>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Contact Email</mat-label>
            <input
              matInput
              required
              placeholder="Contact Email"
              formControlName="contactEmail"
              data-testid="wizard-contact-email"
            />
          </mat-form-field>
          <app-information-box warning pre>
            <div>
              <p>
                The names and descriptions of public projects are visible to all
                other SyRF users. Private projects are hidden from public
                listings (they can only be joined by invitation). This can
                always be changed later in project settings.
              </p>
            </div>
          </app-information-box>
          <mat-form-field appearance="outline" floatLabel="always">
            <mat-label>Project Visibility</mat-label>
            <input
              type="text"
              class="hidden"
              matInput
              formControlName="isPrivate"
            />
            <mat-checkbox
              formControlName="isPrivate"
              color="primary"
              #privateProjectCheckbox
              >Private
            </mat-checkbox>
            <mat-hint>
              <mat-icon class="hint-symbol">
                {{
                  privateProjectCheckbox.checked
                    ? 'visibility_off'
                    : 'visibility'
                }}</mat-icon
              >
              {{
                privateProjectCheckbox.checked
                  ? 'Your project will be private and hidden from public project listings.'
                  : 'Your project will be shown in public project listings.'
              }}
            </mat-hint>
          </mat-form-field>
        </div>
        <div class="wizard-actions">
          <button mat-button syrfDialogClose data-testid="wizard-cancel-btn">Cancel</button>
          <button
            mat-button
            matStepperNext
            [disabled]="createProjectForm.get('basic').invalid"
            type="button"
            data-testid="wizard-next-btn"
          >
            Next
          </button>
        </div>
      </mat-step>
      @if (createProjectForm.get('screening').enabled) {
      <mat-step
        completed="false"
        fxLayout="column"
        label="Screening"
        formGroupName="screening"
        [stepControl]="createProjectForm.get('screening')"
      >
        <div fxLayout="column">
          <mat-form-field appearance="outline">
            <textarea
              matInput
              placeholder="Inclusion Criteria"
              formControlName="inclusionCriteria"
            >
            </textarea>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <textarea
              matInput
              placeholder="Exclusion Criteria"
              formControlName="exclusionCriteria"
            >
            </textarea>
          </mat-form-field>
        </div>
        <div class="wizard-actions">
          <button mat-button syrfDialogClose>Cancel</button>
          <button mat-button matStepperPrevious type="button">Back</button>
          <button
            mat-raised-button
            color="primary"
            type="button"
            [disabled]="!createProjectForm.valid"
            (click)="createProject()"
            matStepperNext
            data-testid="wizard-create-btn"
          >
            Create project
          </button>
        </div>
      </mat-step>
      } @if (createProjectForm.get('livingSearch').enabled) {
      <mat-step
        completed="false"
        fxLayout="column"
        label="Living Search"
        formGroupName="livingSearch"
        [stepControl]="createProjectForm.get('livingSearch')"
      >
        <div fxLayout="column">
          <mat-slide-toggle formControlName="livingSearch"
            >Turn on
          </mat-slide-toggle>
          <mat-form-field appearance="outline">
            <input
              matInput
              placeholder="Search String"
              formControlName="searchString"
            />
          </mat-form-field>
          <mat-form-field
            matTooltip="The search string used to retrieve published studies"
            matTooltipPosition="below"
            appearance="outline"
          >
            <mat-select
              placeholder="Search Engine"
              formControlName="searchEngine"
            >
              <mat-option value="pubmed">PubMed</mat-option>
            </mat-select>
          </mat-form-field>
        </div>
        <div class="wizard-actions">
          <button mat-button matStepperPrevious type="button">Back</button>
          <button mat-button matStepperNext type="button">Next</button>
        </div>
      </mat-step>
      } @if (createProjectForm.get('machineLearning').enabled) {
      <mat-step
        completed="false"
        fxLayout="column"
        label="Machine Learning"
        formGroupName="machineLearning"
        [stepControl]="createProjectForm.get('machineLearning')"
      >
        <mat-slide-toggle formControlName="machineLearning"
          >Turn on
        </mat-slide-toggle>
        <div class="wizard-actions">
          <button mat-button syrfDialogClose>Cancel</button>
          <button mat-button matStepperPrevious type="button">Back</button>
          <button
            mat-raised-button
            type="button"
            [disabled]="!createProjectForm.valid"
            (click)="createProject()"
            color="primary"
            data-testid="wizard-create-btn"
          >
            Create project
          </button>
        </div>
      </mat-step>
      }
      <mat-step>
        <ng-template matStepLabel>Project Setup</ng-template>
        @if (projectLoading$ | async) {
        <div fxLayout="column" fxLayoutAlign="center center">
          <mat-spinner [diameter]="30"></mat-spinner>
        </div>
        }
        <app-project-setup
          [ngClass]="{ remove: (projectLoading$ | async) }"
          [onDialog]="true"
          (dismissTasks)="onDismissDialog()"
        ></app-project-setup>
      </mat-step>
    </mat-horizontal-stepper>
  </form>
</syrf-dialog-layout>
